import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { hooks } from "../hooks";
import { custom } from "../custom";
import { theme } from "../constants";
import { components } from "../components";
import useApi from "../customHook/useApi";
import { updateLeadProfile, viewLeadProfile } from "../environment";
import { callService } from "../customHook/callService";
import { toast } from "react-toastify";
import SuccessModal from "../components/nodataFound/SuccessModal";

type Profile = {
  leadId: string | null;
  full_name: string | null;
  email: string | null;
  phone_number: string | null;
  gender: string | null;
  dob: string | null;
  pref_budget_min_range: number | null;
  pref_budget_max_range: number | null;
  pref_city_name: string | null;
  pref_city_id: string | null;
  pref_locality_name: string | null;
  pref_locality_id: string | null;
};

export interface profileResponse {
  success: boolean;
  statusCode: number;
  data: Profile;
  message?: string;
}

export const ProfileEdit: React.FC = () => {
  const { pathname } = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
    };

    scrollToTop();
  }, [pathname]);

  const navigate = hooks.useNavigate();

  const [formData, setFormData] = useState({
    leadId: "",
    full_name: "",
    email: "",
    phone_number: "",
    gender: "",
    dob: "",
    pref_budget_min_range: "",
    pref_budget_max_range: "",
    pref_city_name: "",
    pref_city_id: "",
    pref_locality_name: "",
    pref_locality_id: "",
  });

  const [fetchFirstLocality, setFetchFirstLocality] = useState("");

  const formatDateForInput = (dob: string | null): string => {
    if (!dob || typeof dob !== "string") return "";

    const delimiter = dob.includes("/") ? "/" : "-";
    const [part1, part2, part3] = dob.split(delimiter);
    if (!part1 || !part2 || !part3) return "";

    return `${part3}-${part2.padStart(2, "0")}-${part1.padStart(2, "0")}`;
  };

  const cityOptions = [
    { id: 1, key: "Delhi" },
    { id: 2, key: "Noida" },
    { id: 3, key: "Gurugram" },
  ];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === "pref_city_name") {
      const selectedCity = cityOptions.find((city) => city.key === value);

      setFormData((prev) => ({
        ...prev,
        pref_city_name: value,
        pref_city_id: selectedCity ? selectedCity.id.toString() : "",
        // pref_city_id: selectedCity ? selectedCity.id.toString() : "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // UPDATE PROFILE API
  const {
    data: UpdateProfileData,
    loading: UpdateProfileLoading,
    callApi: updatePayload,
  } = useApi<profileResponse>(updateLeadProfile, {
    method: "POST",
    autoCall: false,
    dependencies: [],
  });

  useEffect(() => {
    if (UpdateProfileData?.success) {
      // console.log("Profile updated successfully");
      // Optionally show toast/snackbar/redirect
      // toast.success("Profile updated successfully");
      localStorage.setItem("selectedCity", formData.pref_city_name);
      localStorage.setItem("selectedCityId", formData.pref_city_id);
      setIsModalOpen(true);
    }
  }, [UpdateProfileData]);

  // Lead api Call
  useEffect(() => {
    const fetchViewProfile = async () => {
      try {
        const response = await callService("PROFILE_COMPLETED");
        // console.log("✅ PROFILE_COMPLETED success:", response);
      } catch (error) {
        console.error("❌ PROFILE_COMPLETED failed:", error);
      }
    };
    fetchViewProfile();
  }, []);

  // View Profile API CALL
  const {
    data: profileData,
    loading: profileLoading,
    callApi: fetchProfile,
  } = useApi<profileResponse>(viewLeadProfile, {
    method: "POST",
    autoCall: false,
    dependencies: [],
  });

  // 1. API Call Trigger (on mount)
  useEffect(() => {
    const leadId = localStorage.getItem("lead_profile_id") ?? "";
    if (leadId) {
      fetchProfile({ leadId });
    }
  }, []);

  // 2. Populate formData when API response arrives
  useEffect(() => {
    if (profileData?.data) {
      const data = profileData.data;

      const lead_profile_id = localStorage.getItem("lead_profile_id") ?? "";
      setFormData({
        leadId: lead_profile_id ?? "",
        full_name: data.full_name ?? "",
        email: data.email ?? "",
        phone_number: data.phone_number ?? "",
        gender: data.gender ?? "",
        dob: data.dob || "",
        pref_budget_min_range: String(data.pref_budget_min_range ?? ""),
        pref_budget_max_range: String(data.pref_budget_max_range ?? ""),
        pref_city_name: data.pref_city_name ?? "",
        pref_city_id: formData.pref_city_id ?? "",
        pref_locality_name: formData.pref_locality_name ?? "",
        pref_locality_id: formData.pref_locality_id ?? "",
      });
    }
  }, [profileData]); // ✅ Dependency is profileData

  useEffect(() => {
    if (UpdateProfileData) {
      // Optionally check for a success flag here if your API returns one
      // console.log("Update successful, now fetching updated profile...");
      fetchProfile({ leadId: formData.leadId }); // or whatever payload `viewLeadProfile` needs
    }
  }, [UpdateProfileData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const localityDataRaw = localStorage.getItem("selectedLocalityFull");

    // ✅ Store gender values in sessionStorage
    const genderAPI = formData.gender || "";
    let genderReadable = "";

    switch (genderAPI) {
      case "GENDER_MALE":
        genderReadable = "Male";
        break;
      case "GENDER_FEMALE":
        genderReadable = "Female";
        break;
      case "GENDER_OTHER":
        genderReadable = "Other";
        break;
      default:
        genderReadable = "";
    }

    localStorage.setItem("genderAPI", genderAPI);
    localStorage.setItem("gender", genderReadable);

    // ✅ Update city in localStorage
    localStorage.setItem("selectedCity", formData.pref_city_name);
    localStorage.setItem("selectedCityId", formData.pref_city_id);

    // ✅ Remove selected locality and lat/lng from localStorage
    localStorage.removeItem("selectedLocalityFull");
    localStorage.removeItem("selectedLatLng");
    localStorage.removeItem("selectedLocality");

    console.log(formData, " print formData");

    const payload = {
      leadId: formData.leadId,
      full_name: formData.full_name,
      email: formData.email,
      phone_number: formData.phone_number,
      gender: formData.gender, // this is the enum string
      dob: formData.dob, // assumed in dd/mm/yyyy format
      pref_budget_min_range: formData.pref_budget_min_range,
      pref_budget_max_range: formData.pref_budget_max_range,
      pref_city_id: Number(formData.pref_city_id),
      pref_locality_id: fetchFirstLocality, // already assumed number
    };

    console.log("Submitting Payload:", payload);
    updatePayload(payload);
  };

  // ***********************  Start Component  ***************************************
  const renderImageBackground = (): JSX.Element => {
    return (
      <img
        src={require("../assets/other/01.png")}
        alt="Sign In"
        style={{
          width: "100%",
          height: "100vh",
          position: "fixed",
          top: 0,
          left: "50%",
          zIndex: -1,
          objectFit: "cover",
          maxWidth: 650,
          transform: "translateX(-50%)",
        }}
      />
    );
  };

  const renderHeader = (): JSX.Element => {
    return (
      <components.Header
        title="Profile Edit"
        goBack={true}
        style={{ backgroundColor: theme.colors.transparent }}
      />
    );
  };

  const renderContent = (): JSX.Element => {
    // const cityOptions = [
    //   { id: 1, key: "Delhi" },
    //   { id: 2, key: "Noida" },
    //   { id: 3, key: "Gurugram" },
    // ];

    return (
      <main style={{ paddingBottom: 0 }}>
        <form onSubmit={handleSubmit} className="form-container">
          <div className="form-group">
            <label className="form-label">Name</label>
            <input
              type="text"
              name="full_name"
              className="form-input"
              placeholder="Enter Name"
              value={formData.full_name ?? ""}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              name="email"
              className="form-input"
              placeholder="Enter Email"
              value={formData.email ?? ""}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Phone Number</label>
            <input
              disabled
              type="text"
              name="phone_number"
              className="form-input"
              placeholder="Enter Phone number"
              maxLength={10}
              value={formData.phone_number ?? ""}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Gender</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  name="gender"
                  value="Male"
                  checked={formData.gender === "Male"}
                  onChange={handleChange}
                  style={{ accentColor: "#014d4e" }}
                />
                <span>Male</span>
              </label>

              <label>
                <input
                  type="radio"
                  name="gender"
                  value="Female"
                  checked={formData.gender === "Female"}
                  onChange={handleChange}
                  style={{ accentColor: "#014d4e" }}
                />
                <span>Female</span>
              </label>

              <label>
                <input
                  type="radio"
                  name="gender"
                  value="Others"
                  checked={formData.gender === "Others"}
                  onChange={handleChange}
                  style={{ accentColor: "#014d4e" }}
                />
                <span>Other</span>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Date of Birth</label>
            <input
              type="date"
              className="form-input"
              name="dob"
              onClick={(e) => e.currentTarget?.showPicker?.()}
              // value={formatDateForInput(formData.dob)}
              value={formData.dob ?? ""}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Min Budget Preference</label>
            <input
              type="number"
              name="pref_budget_min_range"
              className="form-input"
              placeholder="Enter Min Budget"
              value={formData.pref_budget_min_range ?? ""}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Max Budget Preference</label>
            <input
              type="number"
              name="pref_budget_max_range"
              className="form-input"
              placeholder="Enter Max Budget"
              value={formData.pref_budget_max_range ?? ""}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Preferred City</label>
            <select
              name="pref_city_name"
              className="form-input"
              // value={formData.pref_city_name}
              value={formData.pref_city_name ?? ""}
              onChange={handleChange}
            >
              <option value="">Select City</option>
              {cityOptions.map((city) => (
                <option key={city.id} value={city.key}>
                  {city.key}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Preferred Locality</label>
            <input
              disabled
              type="text"
              name="pref_locality_name"
              className="form-input"
              placeholder="Enter Preferred City"
              value={formData.pref_locality_name}
              onChange={handleChange}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "right" }}>
            <button
              className="form-button"
              type="submit"
              disabled={UpdateProfileLoading}
            >
              {/* {UpdateProfileLoading ? (<div className="loaderButton"></div>) : <>Update Profile</> } */}
              {UpdateProfileLoading ? <>Updating...</> : <>Update Profile</>}
            </button>
          </div>
        </form>

        <SuccessModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          message="Profile updated successfully!"
          subMessage="Your changes have been saved."
          imgSrc={require("../assets/nestfin/checklist.png")}
        />
      </main>
    );
  };

  return (
    <>
      {renderHeader()}
      {renderContent()}
    </>
  );
};
